import { useRef, useState } from 'react'
import { Button } from 'blaze-ui/Button/Button'
import { Edit1 } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ProseInput } from 'common/ProseInput/ProseInput'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import {
  convertMarkdownToHtml,
  convertHtmlToMarkdown,
  ProfileSection,
} from 'screens/BrandKits/components/BrandProfile/markdownUtils'
import { brandProfileSchema } from 'screens/BrandKits/components/BrandProfile/brandProfileSchema'
import { Pressable, useInteractOutside } from '@react-aria/interactions'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { EditableSectionDrawer } from './EditableSectionDrawer'

interface EditableSectionProps {
  section: ProfileSection
  onSectionUpdate: (title: string, newContent: string) => void
  isUpdating: boolean
  isEditing: boolean
  onEdit: () => void
  onCancel: () => void
  isDisabled: boolean
  className?: string
  updateOnChange?: boolean
  saveButtonText?: string
  sectionClassName?: string
  headerClassName?: string
  actionsClassName?: string
  editButtonClassName?: string
  contentClassName?: string
  editingClassName?: string
  editorClassName?: string
}

export const EditableSection = ({
  section,
  onSectionUpdate,
  isUpdating,
  isEditing,
  onEdit,
  onCancel,
  isDisabled,
  updateOnChange = false,
  saveButtonText = 'Save',
  className = '',
  sectionClassName = '',
  headerClassName = '',
  actionsClassName = '',
  editButtonClassName = '',
  contentClassName = '',
  editingClassName = '',
  editorClassName = '',
}: EditableSectionProps) => {
  const [originalHtmlContent, setOriginalHtmlContent] = useState('')
  const [currentHtmlContent, setCurrentHtmlContent] = useState('')
  const editableSectionRef = useRef<HTMLDivElement>(null)
  const { isMobile, isIOSNative } = useResponsiveDesign()
  const { drawersStore } = useRootStore()

  const handleEdit = () => {
    const htmlContent = convertMarkdownToHtml(section.content, brandProfileSchema)
    setOriginalHtmlContent(htmlContent)
    setCurrentHtmlContent(htmlContent)
    onEdit()
  }

  const handleUpdate = (html?: string) => {
    const sourceHtml = html ?? currentHtmlContent
    // Only update if content actually changed
    if (sourceHtml === originalHtmlContent) {
      return
    }
    const markdownContent = convertHtmlToMarkdown(sourceHtml, brandProfileSchema)
    onSectionUpdate(section.title, markdownContent)
  }

  const handleSave = () => {
    handleUpdate()
    onCancel()
  }

  const handleCancel = () => {
    onCancel()
  }

  const handleContentChange = (htmlContent: string) => {
    setCurrentHtmlContent(htmlContent)
    if (updateOnChange) {
      handleUpdate(htmlContent)
    }
  }

  const handleMobileContentClick = () => {
    if (isEditing || isDisabled) return
    if (updateOnChange && (isMobile || isIOSNative)) {
      handleEdit()
    } else if (isMobile) {
      drawersStore.openDrawer(EditableSectionDrawer, {
        section,
        onSectionUpdate,
        isUpdating,
      })
    }
  }

  const handleClickOutside = () => {
    if (!isEditing || isUpdating) return

    handleSave()
  }

  useInteractOutside({ ref: editableSectionRef, onInteractOutside: handleClickOutside })

  return (
    <div className={`${className} ${sectionClassName}`.trim()} ref={editableSectionRef}>
      <div className={`${headerClassName}`.trim()}>
        <Heading level={2}>{section.title}</Heading>
        <ResponsiveDesign.Desktop>
          {isEditing ? (
            <div className={`${actionsClassName}`.trim()}>
              <Button variant="editor" size="sm" onPress={handleCancel} isDisabled={isUpdating}>
                Cancel
              </Button>
              <Button variant="ghost" size="sm" onPress={handleSave} isDisabled={isUpdating}>
                {isUpdating ? 'Saving...' : saveButtonText}
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              frontIcon={Edit1}
              onPress={handleEdit}
              className={editButtonClassName}
              isDisabled={isDisabled}
            >
              Edit
            </Button>
          )}
        </ResponsiveDesign.Desktop>
      </div>

      {isEditing ? (
        <div className={`${editingClassName}`.trim()}>
          <ProseInput
            initialHtmlString={currentHtmlContent}
            onChange={handleContentChange}
            placeholder="Enter section content..."
            className={editorClassName}
            schema={brandProfileSchema}
            autoFocus={isIOSNative}
          />
        </div>
      ) : (
        <Pressable onPress={handleMobileContentClick}>
          <div className={`${contentClassName}`.trim()}>
            <ProseDisplay
              html={convertMarkdownToHtml(section.content, brandProfileSchema)}
              schema={brandProfileSchema}
            />
          </div>
        </Pressable>
      )}
    </div>
  )
}
